import { useCallback } from "react";
import {
    createViewSupportTickets,
    createViewSupportTicketDetail,
    createViewForwardedTickets,
} from "@/ui/views";
import type { TCompositionDeps } from "@/ui/compositionRoot/types";

export function wireSupport({ useAppStore, api }: TCompositionDeps) {
    const ViewSupportTickets = createViewSupportTickets({
        useCases: () => {
            const effectiveCompanyId = useAppStore(
                (state) => state.effectiveCompanyId ?? state.user?.companyId,
            );
            const loadTickets = useCallback(
                async (params: { page: number; pageSize: number; status?: string }) => {
                    const result = await api.supportTickets.list({
                        page: params.page,
                        page_size: params.pageSize,
                        status: params.status as
                            | "open"
                            | "in_review"
                            | "needs_info"
                            | "rejected"
                            | "forwarded"
                            | "closed"
                            | undefined,
                    });
                    return { items: result.items, total: result.total };
                },
                // eslint-disable-next-line react-hooks/exhaustive-deps
                [effectiveCompanyId],
            );
            return { loadTickets };
        },
    });

    const ViewSupportTicketDetail = createViewSupportTicketDetail({
        useCases: () => {
            const storeUser = useAppStore((state) => state.user);
            const isViewingAsAdmin = useAppStore((state) => state.user?.role === "admin");

            const loadTicket = useCallback(async (id: string) => api.supportTickets.get(id), []);
            const loadComments = useCallback(async (id: string) => {
                const res = await api.supportTickets.listComments(id);
                return res.items;
            }, []);
            const addComment = useCallback(async (id: string, body: string) => {
                await api.supportTickets.createComment(id, { body });
            }, []);
            const closeTicket = useCallback(async (id: string) => {
                await api.supportTickets.close(id);
            }, []);
            const updateTicket = useCallback(
                async (
                    id: string,
                    data: {
                        title?: string;
                        description?: string;
                        ticket_type?: "bug" | "feature";
                        priority?: "low" | "normal" | "high";
                    },
                ) => {
                    await api.supportTickets.update(id, data);
                },
                [],
            );
            const adminActionFn = useCallback(
                async (
                    id: string,
                    action: "reopen" | "in_review" | "needs_info" | "reject" | "forward",
                ) => {
                    await api.supportTickets.adminAction(id, { action });
                },
                [],
            );

            return {
                loadTicket,
                loadComments,
                addComment,
                closeTicket,
                updateTicket,
                adminAction: adminActionFn,
                isAdmin: isViewingAsAdmin,
                isProdaAdmin: useAppStore((state) => {
                    const company = state.availableCompanies.find(
                        (c) => c.id === (state.effectiveCompanyId ?? state.user?.companyId),
                    );
                    const companyName = company?.name ?? "";
                    const companyCode = company?.code ?? "";
                    return (
                        isViewingAsAdmin &&
                        (companyName.toLowerCase() === "proda" ||
                            companyCode.toUpperCase() === "PRODA")
                    );
                }),
                currentUserId: storeUser?.id ?? null,
                canManageReports: storeUser?.permissions?.includes("report_bug_edit") ?? false,
            };
        },
    });

    const ViewForwardedTickets = createViewForwardedTickets({
        useCases: () => {
            const loadForwardedTickets = useCallback(
                async (params: { page: number; pageSize: number; companyId?: string }) => {
                    const result = await api.supportTickets.listForwarded({
                        page: params.page,
                        page_size: params.pageSize,
                        company_id: params.companyId,
                    });
                    return { items: result.items, total: result.total };
                },
                [],
            );
            return { loadForwardedTickets };
        },
    });

    return { ViewSupportTickets, ViewSupportTicketDetail, ViewForwardedTickets };
}
